import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { updateUserProfile } from "../views/utilities/ApiCalls";
import { useModel } from "../hooks/modelcontex";

const DOBModal = ({ isOpen, onClose, onSuccess }) => {
  const { Myprofile } = useModel();
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Generate options for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => currentYear - i);

  // Validate date
  const isValidDate = (d, m, y) => {
    if (!d || !m || !y) return false;
    const date = new Date(y, m - 1, d);
    return (
      date.getFullYear() === parseInt(y) &&
      date.getMonth() === m - 1 &&
      date.getDate() === parseInt(d)
    );
  };

  // Get days in month (handles leap years)
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  // Update days when month/year changes
  useEffect(() => {
    if (month && year) {
      const maxDays = getDaysInMonth(parseInt(month), parseInt(year));
      if (day && parseInt(day) > maxDays) {
        setDay("");
      }
    }
  }, [month, year, day]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!day || !month || !year) {
      setError("Please select day, month, and year");
      return;
    }

    if (!isValidDate(day, month, year)) {
      setError("Please select a valid date");
      return;
    }

    // Check age range (1-120)
    const age = currentYear - parseInt(year);
    if (age < 1 || age > 120) {
      setError("Age must be between 1 and 120 years");
      return;
    }

    // Format DOB as "YYYY-MM-DD"
    const formattedDOB = `${year}-${String(month).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;

    setLoading(true);

    try {
      const response = await updateUserProfile({ dob: formattedDOB });

      if (response.status === "Success" || response.status === "success") {
        // Refresh profile data
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        setError(response.message || "Failed to update date of birth");
      }
    } catch (err) {
      console.error("Error updating DOB:", err);
      setError("An error occurred while updating your date of birth");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const maxDays = month && year ? getDaysInMonth(parseInt(month), parseInt(year)) : 31;
  const availableDays = days.filter((d) => d <= maxDays);

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalHeader>
          <Title>Enter Your Date of Birth</Title>
          <Subtitle>Please provide your date of birth to continue</Subtitle>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <DropdownsContainer>
            <DropdownWrapper>
              <Label>Day</Label>
              <Select
                value={day}
                onChange={(e) => setDay(e.target.value)}
                required
              >
                <option value="">DD</option>
                {availableDays.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </DropdownWrapper>

            <DropdownWrapper>
              <Label>Month</Label>
              <Select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                required
              >
                <option value="">MM</option>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </DropdownWrapper>

            <DropdownWrapper>
              <Label>Year</Label>
              <Select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                required
              >
                <option value="">YYYY</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </DropdownWrapper>
          </DropdownsContainer>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonContainer>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </SubmitButton>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default DOBModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: var(--color-surface);
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 500px;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--color-border);

  @media (max-width: 768px) {
    padding: 20px;
    max-width: 90%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-text-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-background);
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 25px;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 8px 0;
  font-family: "Merriweather", serif;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--color-text-tertiary);
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DropdownsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background-color: var(--color-background);
  color: var(--color-text);
  font-size: 14px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--color-primary);
  }

  &:hover {
    border-color: var(--color-primary);
  }

  option {
    background-color: var(--color-background);
    color: var(--color-text);
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background-color: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.3);
  border-radius: 8px;
  color: #ff4444;
  font-size: 14px;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const SubmitButton = styled.button`
  padding: 12px 32px;
  background-color: var(--color-primary);
  color: var(--color-surface);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  min-width: 120px;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

